import NotificationApiService from "services/notification/NotificationApiService";
import { notifyChannelUpdate } from "services/firebase/firestoreUtils";

/**
 * Agrupa mensagens por data (YYYY-MM-DD) a partir do createdAt.
 * @param {Array} messages - Lista de mensagens
 * @returns {Object} - { [dateKey]: messages[] }
 */
export function groupMessagesByDate(messages) {
  const byDate = {};
  if (!Array.isArray(messages)) return byDate;
  for (const msg of messages) {
    const raw = msg.createdAt || msg.created_at || "";
    const dateKey =
      (typeof raw === "string" && (raw.split(" ")[0] || raw.split("T")[0])) ||
      "unknown";
    if (!byDate[dateKey]) byDate[dateKey] = [];
    byDate[dateKey].push(msg);
  }
  return byDate;
}

/**
 * Retorna o id da mensagem mais antiga para paginação.
 * @param {Object} messagesByDate - { [dateKey]: messages[] }
 * @returns {number|undefined}
 */
export function getOldestMessageId(messagesByDate) {
  if (!messagesByDate || typeof messagesByDate !== "object") return undefined;
  let oldestCreatedAt;
  let oldestId;
  for (const dateKey of Object.keys(messagesByDate)) {
    const list = messagesByDate[dateKey] || [];
    for (const msg of list) {
      const createdAt = msg?.createdAt || msg?.created_at;
      if (
        createdAt &&
        msg?.id != null &&
        (!oldestCreatedAt || String(createdAt) < String(oldestCreatedAt))
      ) {
        oldestCreatedAt = createdAt;
        oldestId = msg.id;
      }
    }
  }
  return oldestId;
}

/**
 * Retorna o id da mensagem mais recente para paginação.
 * @param {Object} messagesByDate - { [dateKey]: messages[] }
 * @returns {number|undefined}
 */
export function getNewestMessageId(messagesByDate) {
  if (!messagesByDate || typeof messagesByDate !== "object") return undefined;
  let newestCreatedAt;
  let newestId;
  for (const dateKey of Object.keys(messagesByDate)) {
    const list = messagesByDate[dateKey] || [];
    for (const msg of list) {
      const createdAt = msg?.createdAt || msg?.created_at;
      if (
        createdAt &&
        msg?.id != null &&
        (!newestCreatedAt || String(createdAt) > String(newestCreatedAt))
      ) {
        newestCreatedAt = createdAt;
        newestId = msg.id;
      }
    }
  }
  return newestId;
}

/**
 * Retorna a data mais antiga no formato Y-m-d H:i:s para beforeDate.
 * @param {Object} messagesByDate - { [dateKey]: messages[] }
 * @returns {string|undefined}
 */
export function getOldestMessageDate(messagesByDate) {
  if (!messagesByDate || typeof messagesByDate !== "object") return undefined;
  let oldest = null;
  for (const dateKey of Object.keys(messagesByDate)) {
    const list = messagesByDate[dateKey] || [];
    for (const msg of list) {
      const createdAt = msg?.createdAt || msg?.created_at;
      if (createdAt) {
        const d = new Date(createdAt);
        if (Number.isFinite(d.getTime()) && (!oldest || d < oldest)) oldest = d;
      }
    }
  }
  if (!oldest) return undefined;
  return oldest.toISOString().slice(0, 19).replace("T", " ");
}

/**
 * Retorna a data mais recente no formato Y-m-d H:i:s para afterDate.
 * @param {Object} messagesByDate - { [dateKey]: messages[] }
 * @returns {string|undefined}
 */
export function getNewestMessageDate(messagesByDate) {
  if (!messagesByDate || typeof messagesByDate !== "object") return undefined;
  let newest = null;
  for (const dateKey of Object.keys(messagesByDate)) {
    const list = messagesByDate[dateKey] || [];
    for (const msg of list) {
      const createdAt = msg?.createdAt || msg?.created_at;
      if (createdAt) {
        const d = new Date(createdAt);
        if (Number.isFinite(d.getTime()) && (!newest || d > newest)) newest = d;
      }
    }
  }
  if (!newest) return undefined;
  return newest.toISOString().slice(0, 19).replace("T", " ");
}

function normalizeChannelUnread(raw) {
  const unread = Number(raw.unread) || Number(raw.unread_count) || 0;
  return { ...raw, id: String(raw.id), unread };
}

class ChannelService {
  /**
   * Lista canais (paginado, filtro por nome).
   * @param {Object} params - { name?, per_page?, page? }
   * @returns {Promise<{ data: Array, meta?: Object }>}
   */
  async getChannels(params = {}) {
    const response = await NotificationApiService.get("/api/chat/list", {
      params: {
        name: params.name || undefined,
        per_page: params.per_page || 100,
        page: params.page || 1,
      },
    });
    const data = response.data?.data ?? [];
    const list = Array.isArray(data)
      ? data.map((c) => normalizeChannelUnread(c))
      : [];
    return { data: list, meta: response.data?.meta, links: response.data?.links };
  }

  /**
   * Mensagens de um canal.
   * Compatível com chamada legada (channelId, isPrivate, options) e chamada nova (channelId, options).
   * @param {string|number} channelId
   * @param {boolean|Object} isPrivateOrOptions - boolean isPrivate ou options
   * @param {Object} maybeOptions - { beforeId?, afterId?, chatId? }
   * @returns {Promise<{ messages: Object, channel?: Object }>}
   */
  async getMessages(channelId, isPrivateOrOptions = {}, maybeOptions = {}) {
    const channelIdStr = String(channelId);
    const isPrivate =
      typeof isPrivateOrOptions === "boolean"
        ? isPrivateOrOptions
        : isPrivateOrOptions?.isPrivate === true;
    const options =
      typeof isPrivateOrOptions === "boolean"
        ? maybeOptions || {}
        : isPrivateOrOptions || {};
    const params = {
      channelId: channelIdStr,
      ...(isPrivate === true && { isPrivate: "true" }),
      ...(options.beforeId != null && { beforeId: options.beforeId }),
      ...(options.afterId != null && { afterId: options.afterId }),
      ...(options.chatId && { chatId: options.chatId }),
    };
    const response = await NotificationApiService.get("/api/chat", { params });
    const data = response.data;

    if (Array.isArray(data)) {
      return {
        messages: groupMessagesByDate(data),
        channel: undefined,
      };
    }

    const dataObj = data?.data;
    // Formato comum: { data: [ ...mensagens ] }
    if (Array.isArray(dataObj)) {
      return {
        messages: groupMessagesByDate(dataObj),
        channel: data?.channel,
      };
    }

    // Formato eventual: { messages: [ ... ] } no primeiro nível
    if (Array.isArray(data?.messages)) {
      return {
        messages: groupMessagesByDate(data.messages),
        channel: data?.channel,
      };
    }

    const messagesFromObj =
      (dataObj && typeof dataObj === "object" && dataObj.messages) ||
      data?.messages;
    const channelFromObj =
      (dataObj && typeof dataObj === "object" && dataObj.channel) ||
      data?.channel;

    return {
      messages: messagesFromObj || {},
      channel: channelFromObj,
    };
  }

  /**
   * Enviar mensagem de texto.
   * @param {Object} messageData - { message, channelId, authorName?, authorId?, reference? }
   * @param {string} [channelName] - Nome do canal para Firestore
   * @returns {Promise<*>}
   */
  async sendMessage(messageData, channelName) {
    const payload = {
      channelId: Number(messageData.channelId),
      reference: messageData.reference != null ? Number(messageData.reference) : null,
      message: messageData.message || "",
    };
    const response = await NotificationApiService.post("/api/chat", payload);
    // Fire-and-forget: não bloqueia o retorno ao caller
    notifyChannelUpdate(
      String(messageData.channelId),
      {
        content: messageData.message,
        authorName: messageData.authorName || "Usuário",
        authorId: String(messageData.authorId || "0"),
      },
      channelName
    ).catch((err) =>
      console.warn("Erro ao notificar Firestore, mas mensagem foi salva:", err)
    );
    return response.data?.data;
  }

  /**
   * Deletar mensagem.
   * @param {number} id - ID da mensagem
   * @returns {Promise<*>}
   */
  async deleteMessage(id) {
    const response = await NotificationApiService.delete(`/api/chat/${id}`);
    return response.data;
  }

  /**
   * Denunciar mensagem.
   * @param {number} id - ID da mensagem
   * @param {Object} data - { type: 1-4, description }
   * @returns {Promise<*>}
   */
  async reportMessage(id, data) {
    const response = await NotificationApiService.post(
      `/api/chat/${id}/report`,
      data
    );
    return response.data;
  }

  /**
   * Compartilhar wiki em canais.
   * @param {Object} data - { message, sharedWikiId, channelIds: number[] }
   * @returns {Promise<*>}
   */
  async shareWiki(data) {
    const response = await NotificationApiService.post(
      "/api/chat/share-wiki",
      data
    );
    return response.data;
  }

  /**
   * Adicionar reação emoji à mensagem.
   * @param {Object} data - { chatId, emojiId }
   * @returns {Promise<*>}
   */
  async addEmoji(data) {
    const response = await NotificationApiService.post("/api/emoji-chat", data);
    return response.data;
  }
}

export default new ChannelService();
